import {
  formatContractChangedPush,
  formatContractPush,
  formatIntentPush,
  formatMessagePush,
  formatPresencePush,
} from "@br9704/aethereum-shared";
import { HostedSubscriber, startPresenceHeartbeat, type SubscriberDelivery } from "./subscriber.js";

/**
 * The hosted push pipeline: stream deliveries → one-line session injections.
 * This is what makes the "gasp moment" work cross-machine — a teammate's
 * contract change lands in your live session without anyone calling a tool.
 */

/** Format a stream delivery as a single injectable line; null = don't push. */
export function formatHostedPush(d: SubscriberDelivery): string | null {
  const e = d.event;
  const who = d.handle ?? ("memberId" in e ? e.memberId.slice(0, 8) : "teammate");
  switch (e.type) {
    case "contract_changed":
      return formatContractChangedPush(e);
    case "contract":
      return formatContractPush(who, e.name, e.version, e.shape);
    case "intent":
      return formatIntentPush(who, e.text);
    case "presence":
      // only offline→online transitions reach the events stream
      return formatPresencePush(who, e.building);
    case "message":
      // Broadcasts inject live; directed messages arrive via get_team_context
      // (the prompt hook), so we never leak a directed message to the wrong session.
      return e.to === "all" ? formatMessagePush(e) : null;
    case "chat":
      return null; // chat flows through the room-server path, not the spine
    case "directive":
      // A master directive is a standing order from the operator: inject it LIVE
      // into every running session (this is the telekinetic moment), unless cleared.
      return e.cleared
        ? null
        : `⚡ TEAM DIRECTIVE${d.handle ? ` from ${d.handle}` : ""}: ${e.text} — standing order, follow it now`;
    case "decision":
      return null; // decisions are durable; they surface via get_team_context, not live injection
    case "brief":
      return null; // the project brief surfaces via get_team_context, not live injection
    case "plan":
      return null; // the shared plan surfaces via get_team_context, not live injection
    case "claim":
      return e.status === "held" ? `◆ ${who} is now working on ${e.area} (route around it)` : null;
    case "ticket":
      return null; // tickets surface via get_team_context, not live injection
    case "goal":
      // A new/active goal is reference context; a goal reaching done/blocked is a
      // loop event worth a live nudge so agents stop or re-plan.
      return e.status === "done"
        ? `◎ GOAL DONE${d.handle ? ` (${d.handle})` : ""}: ${e.text} — verify, then stop.`
        : e.status === "blocked"
          ? `◎ GOAL BLOCKED${d.handle ? ` (${d.handle})` : ""}: ${e.text} — re-plan before continuing.`
          : null; // active/abandoned goals surface via get_team_context
    case "verification":
      // A FAIL is the loop's "not done" signal: inject it live so the agent does
      // not declare done. pass/partial surface via get_team_context.
      return e.status === "fail"
        ? `✗ VERIFICATION FAILED${e.target ? ` (${e.target})` : ""}: ${e.summary} — do not call it done.`
        : null;
    case "contract_proposed":
    case "proposal_response":
    case "contract_finalized":
      return null; // negotiation surfaces via get_team_context to the right agents, not a broadcast
    case "question":
      return null; // a question is for the human (dashboard/CLI), not an agent broadcast
    case "answer":
      return null; // the answer rides back to the ASKER via get_team_context, not a broadcast
    case "consumer":
      return null; // a consumer edge quietly refines alert targeting; no live push
    case "collision_averted":
      return null; // a metric event; surfaces on the dashboard, not a live nudge
    case "ruleset":
      // A new team ruleset is a standing rule change worth a live nudge.
      return `⚑ TEAM RULESET updated${d.handle ? ` by ${d.handle}` : ""} (v${e.version}) — run \`aethereum sync-rules\` to pick it up.`;
  }
}

export interface HostedSpine {
  enabled: boolean;
  stop(): void;
}

/**
 * Start the hosted realtime spine when the env carries the hosted room key
 * (same vars the mirror uses). Subscribes to the stream, formats teammate
 * events, hands them to `deliver` (tmux bridge or stdout), and heartbeats
 * presence. Silent no-op without env; silent degrade on any failure.
 *
 *   AETHEREUM_SYNC_API_URL  e.g. https://www.aethereum.dev
 *   AETHEREUM_SYNC_TOKEN    the member-bound room key from the connect flow
 */
export function startHostedSpine(
  deliver: (text: string) => void,
  env: { AETHEREUM_SYNC_API_URL?: string; AETHEREUM_SYNC_TOKEN?: string } = process.env,
  opts: { building?: () => string | undefined; fetchImpl?: typeof fetch } = {},
): HostedSpine {
  const baseUrl = env.AETHEREUM_SYNC_API_URL;
  const token = env.AETHEREUM_SYNC_TOKEN;
  if (!baseUrl || !token) return { enabled: false, stop() {} };

  const subscriber = new HostedSubscriber({
    baseUrl,
    token,
    fetchImpl: opts.fetchImpl,
    onEvent: (d) => {
      const line = formatHostedPush(d);
      if (line) deliver(line);
    },
  });
  void subscriber.start();
  const stopHeartbeat = startPresenceHeartbeat(baseUrl, token, {
    building: opts.building,
    fetchImpl: opts.fetchImpl,
  });

  return {
    enabled: true,
    stop() {
      stopHeartbeat();
      subscriber.stop();
    },
  };
}
